import { useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster, toast } from "sonner";
import { ChevronLeft, MapPin, Stethoscope, Calendar, Plus } from "lucide-react";
import { api } from "../lib/api";
import { Page, Spinner, UrgencyChip, StatusChip } from "../components/Brand";
import { DAYS_FR_SHORT, MONTHS_FR, ymd, LOCATIONS, formatDateFr, add30 } from "../lib/fr";

function buildSlots() {
  const out = [];
  for (let h = 8; h <= 19; h++) {
    for (const m of [0, 30]) {
      out.push(`${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}`);
    }
  }
  return out; // 08:00 → 19:30 inclusive
}

function isValidHHMM(s) {
  if (!/^([01]?\d|2[0-3]):[0-5]\d$/.test(s)) return false;
  return true;
}

export default function OBGYNSchedule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: ref, isLoading } = useQuery({
    queryKey: ["referral", id],
    queryFn: async () => (await api.get(`/referrals/${id}`)).data,
  });

  const { data: providers } = useQuery({
    queryKey: ["obgyn-providers"],
    queryFn: async () => (await api.get("/obgyn/providers")).data,
  });

  const days = useMemo(() => {
    const arr = [];
    const today = new Date(); today.setHours(0,0,0,0);
    for (let i = 0; i < 14; i++) {
      const d = new Date(today); d.setDate(today.getDate() + i);
      arr.push(d);
    }
    return arr;
  }, []);

  const slots = useMemo(buildSlots, []);

  const [date, setDate] = useState(ymd(new Date()));
  const [time, setTime] = useState("");
  const [customMode, setCustomMode] = useState(false);
  const [customTime, setCustomTime] = useState("");
  const [location, setLocation] = useState("");
  const [locationOther, setLocationOther] = useState("");
  const [provider, setProvider] = useState("");
  const [providerOther, setProviderOther] = useState("");
  const [busy, setBusy] = useState(false);

  if (isLoading) return <Page><div className="cs-card p-10 flex justify-center"><Spinner /></div></Page>;
  if (!ref) return <Page><div>Référence introuvable</div></Page>;

  const realLocation = location === "Autres" ? locationOther.trim() : location;
  const realProvider = provider === "Autres" ? providerOther.trim() : provider;
  const effectiveTime = customMode ? customTime.trim() : time;
  const timeOk = !!effectiveTime && isValidHHMM(effectiveTime);
  const canConfirm = date && timeOk && realLocation && realProvider;

  async function confirm() {
    if (!canConfirm) return toast.error("Veuillez compléter date, heure (HH:MM), lieu et praticien");
    setBusy(true);
    try {
      await api.post(`/referrals/${id}/schedule`, {
        appointment_date: date,
        appointment_time: effectiveTime,
        location: realLocation,
        provider: realProvider,
      });
      toast.success("Rendez-vous confirmé");
      queryClient.invalidateQueries({ queryKey: ["obgyn-inbox"] });
      queryClient.invalidateQueries({ queryKey: ["obgyn-agenda"] });
      setTimeout(() => navigate("/obgyn"), 600);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    } finally { setBusy(false); }
  }

  return (
    <Page>
      <Toaster position="top-center" richColors />
      <button onClick={() => navigate(-1)} className="text-[14px] mb-4 inline-flex items-center gap-1 hover:underline"
              style={{ color: "var(--cs-text-muted)" }} data-testid="back-link">
        <ChevronLeft size={16} /> Retour
      </button>

      {/* Summary */}
      <section className="cs-card p-5 md:p-6 mb-6" data-testid="referral-summary">
        <div className="flex items-start justify-between pb-3 border-b" style={{ borderColor: "var(--cs-border)" }}>
          <div>
            <div className="cs-label">Référence</div>
            <div className="font-semibold mt-1" style={{ fontSize: 26, letterSpacing: "-0.5px" }}>{ref.referral_code}</div>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <UrgencyChip value={ref.urgency} />
            <StatusChip value={ref.status} />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4 pt-4 text-[13px]">
          <Field label="Diagnostic" value={ref.diagnosis === "cardiomyopathy" ? "Cardiomyopathie" : "CMP péripartum"} />
          <Field label="FEVG" value={ref.ef_range + " %"} />
          <Field label="Âge" value={ref.age_range + " ans"} />
          <Field label="Grossesse" value={pregLabel(ref.pregnancy_status)} />
          <Field label="Contraception actuelle" value={contraLabel(ref.current_contraception)} />
          <Field label="Plan envisagé" value={contraLabel(ref.contraception_plan)} />
          <Field label="Œstrogènes" value={ref.estrogen_contraindicated ? "Contre-indiqués" : "Autorisés"} />
          <Field label="Référent" value={ref.created_by_name || "—"} />
        </div>
        {ref.notes && (
          <div className="mt-4 p-3 rounded-[12px] text-[13px]" style={{ background: "var(--cs-bg)", color: "var(--cs-text)" }}>
            <div className="cs-label mb-1">Notes</div>
            {ref.notes}
          </div>
        )}
      </section>

      {/* Scheduling form */}
      <section className="cs-card p-5 md:p-6 mb-6" data-testid="schedule-form">
        <h2 className="font-semibold mb-4" style={{ fontSize: 18, letterSpacing: "-0.3px" }}>Programmer un rendez-vous</h2>

        <div className="mb-5">
          <div className="cs-label mb-2 flex items-center gap-1"><Calendar size={12} /> Date</div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2" data-testid="date-chips">
            {days.map((d) => {
              const v = ymd(d);
              const active = v === date;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setDate(v)}
                  className={`cs-pill flex-col items-center min-w-[64px] py-2 ${active ? "active" : ""}`}
                  data-testid={`date-${v}`}
                >
                  <span className="text-[10px] uppercase tracking-wider opacity-70">{DAYS_FR_SHORT[d.getDay()]}</span>
                  <span className="text-[16px] font-semibold leading-tight">{d.getDate()}</span>
                  <span className="text-[10px] opacity-70">{MONTHS_FR[d.getMonth()].slice(0,3)}</span>
                </button>
              );
            })}
          </div>
          {date && <div className="mt-2 text-[13px]" style={{ color: "var(--cs-text-muted)" }}>{formatDateFr(date)}</div>}
        </div>

        <div className="mb-5">
          <div className="cs-label mb-2 flex items-center justify-between">
            <span>Créneau horaire (30 min)</span>
            <button type="button"
                    onClick={() => setCustomMode((v) => !v)}
                    className="text-[11px] font-medium uppercase tracking-wider hover:underline"
                    style={{ color: "var(--cs-primary-dark)", letterSpacing: "0.6px" }}
                    data-testid="toggle-custom-time">
              {customMode ? "← Choisir un créneau" : <><Plus size={11} className="inline -mt-0.5" /> Heure personnalisée</>}
            </button>
          </div>

          {!customMode ? (
            <>
              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid="time-slots">
                {slots.map((t) => (
                  <button
                    type="button"
                    key={t}
                    onClick={() => setTime(t)}
                    className={`cs-time ${time === t ? "active" : ""}`}
                    data-testid={`time-${t}`}
                  >{t}</button>
                ))}
              </div>
              {time && <div className="mt-2 text-[13px]" style={{ color: "var(--cs-text-muted)" }}>{time} → {add30(time)} (30 min)</div>}
            </>
          ) : (
            <div data-testid="custom-time-block">
              <div className="flex items-center gap-3">
                <input
                  type="time"
                  step="60"
                  className="cs-input"
                  style={{ maxWidth: 180 }}
                  value={customTime}
                  onChange={(e) => setCustomTime(e.target.value)}
                  placeholder="HH:MM"
                  data-testid="custom-time-input"
                />
                {customTime && isValidHHMM(customTime) && (
                  <div className="text-[13px]" style={{ color: "var(--cs-text-muted)" }}>
                    {customTime} → {add30(customTime)} (30 min)
                  </div>
                )}
              </div>
              <div className="text-[12px] mt-2" style={{ color: "var(--cs-text-subtle)" }}>
                Saisissez n'importe quelle heure au format <b>HH:MM</b> si aucun créneau standard ne convient.
              </div>
            </div>
          )}
        </div>

        <div className="mb-5">
          <div className="cs-label mb-2 flex items-center gap-1"><MapPin size={12} /> Lieu</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="location-picker">
            {LOCATIONS.map((l) => (
              <button type="button" key={l}
                onClick={() => setLocation(l)}
                className={`cs-picker items-center text-center ${location === l ? "active" : ""}`}
                data-testid={`location-${l}`}>
                <div className="title">{l}</div>
              </button>
            ))}
          </div>
          {location === "Autres" && (
            <input className="cs-input mt-3" placeholder="Préciser le lieu"
                   value={locationOther} onChange={(e) => setLocationOther(e.target.value)}
                   data-testid="location-other-input" />
          )}
        </div>

        <div className="mb-5">
          <div className="cs-label mb-2 flex items-center gap-1"><Stethoscope size={12} /> Praticien</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="provider-picker">
            {[...(providers || []).map((p) => p.name), "Autres"].map((p) => (
              <button type="button" key={p}
                onClick={() => setProvider(p)}
                className={`cs-picker text-center ${provider === p ? "active" : ""}`}
                data-testid={`provider-${p.replace(/\s+/g,"-")}`}>
                <div className="title">{p}</div>
                {p !== "Autres" && providers && (() => {
                  const found = providers.find((x) => x.name === p);
                  return found?.title ? <div className="desc">{found.title}</div> : null;
                })()}
              </button>
            ))}
          </div>
          {(!providers || providers.length === 0) && (
            <div className="text-[12px] mt-2" style={{ color: "var(--cs-text-subtle)" }}>
              Aucun OB-GYN actif n'est encore enregistré dans le système — sélectionnez « Autres » et saisissez un nom.
            </div>
          )}
          {provider === "Autres" && (
            <input className="cs-input mt-3" placeholder="Préciser le nom du praticien"
                   value={providerOther} onChange={(e) => setProviderOther(e.target.value)}
                   data-testid="provider-other-input" />
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
          <button onClick={() => navigate("/obgyn")} className="cs-btn-ghost" data-testid="cancel-schedule">Annuler</button>
          <button onClick={confirm} className="cs-btn-primary" disabled={!canConfirm || busy} data-testid="confirm-schedule">
            {busy ? <Spinner /> : "Confirmer le rendez-vous"}
          </button>
        </div>
      </section>

      <div className="text-center text-[12px]" style={{ color: "var(--cs-text-subtle)" }}>
        Le marquage du statut (vue, manqué, terminé) sera disponible depuis l'<b>Agenda</b> après la programmation.
      </div>
    </Page>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="cs-label">{label}</div>
      <div className="font-medium mt-0.5">{value}</div>
    </div>
  );
}
function pregLabel(v) {
  return ({ not_pregnant: "Non enceinte", pregnant_t1: "T1", pregnant_t2: "T2", pregnant_t3: "T3", postpartum: "Post-partum" })[v] || v;
}
function contraLabel(v) {
  return ({
    none: "Aucune", condom: "Préservatif", copper_iud: "DIU cuivre", hormonal_iud: "DIU hormonal",
    implant: "Implant", progestin_pill: "Pilule progestative", combined_pill: "Pilule combinée",
    dmpa: "DMPA", sterilization: "Stérilisation", none_yet: "À déterminer", permanent: "Méthode permanente",
  })[v] || v;
}
